import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faArchive, faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { postContactArchive } from 'api/contacts/single';
import * as ArchiveContact from 'assets/js/pages/contacts/ArchiveContact';
import { CreatedByLabel } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/components/CreatedByLabel';
import * as ActivityItem from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/ActivityItem';
import { ARCHIVE_REASONS } from 'assets/js/pages/contacts/single/timeline/timeline-list/item/items/archived/archiveReasons';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ArchivedActivity, Contact } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const Archived = ({ item, contact }: { item: ArchivedActivity; contact: Contact | null }) => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const toast = useToast();
    const isArchived = !!contact?.archived_at;

    const { mutate: unarchiveContact } = useMutation({
        mutationFn: (contactId: number) =>
            postContactArchive(accountId, contactId, { isArchived: true }),
        onSuccess: () => {
            toast.success('Unarchived successfully');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message || 'Unable to unarchive');
        },
    });

    return (
        <ActivityItem.Root>
            <ActivityItem.Trigger>
                <ActivityItem.Icon className="bg-orange-50">
                    <FontAwesomeIcon className="text-orange-500" icon={faArchive} />
                </ActivityItem.Icon>
                <p>
                    <CreatedByLabel createdBy={item.created_by} /> archived{' '}
                    <span className="font-medium">
                        {contact?.first_name} {contact?.last_name}
                    </span>
                </p>
            </ActivityItem.Trigger>
            <ActivityItem.Content className="pl-16">
                <div className="bg-gray-25 flex w-full flex-col gap-4 rounded-md px-3 py-2">
                    <div>
                        <span>Reason</span>
                        <p>
                            {item.data.reason ? (
                                ARCHIVE_REASONS[item.data.reason].label
                            ) : (
                                <span className="text-gray-400">N/A</span>
                            )}
                        </p>
                    </div>
                    <div>
                        <span>Notes</span>
                        <p>
                            {item.data.note ? (
                                item.data.note
                            ) : (
                                <span className="text-gray-400">N/A</span>
                            )}
                        </p>
                    </div>
                </div>
                {!!contact?.id && (
                    <div className="flex justify-end gap-4">
                        <ArchiveContact.Root activity={item}>
                            <ArchiveContact.Trigger asChild>
                                <Button variant="outline" color="neutral">
                                    <FontAwesomeIcon icon={faEdit} />
                                    Edit
                                </Button>
                            </ArchiveContact.Trigger>
                        </ArchiveContact.Root>
                        {isArchived && (
                            <Button variant="solid" onClick={() => unarchiveContact(contact.id)}>
                                Unarchive
                            </Button>
                        )}
                    </div>
                )}
            </ActivityItem.Content>
        </ActivityItem.Root>
    );
};

export { Archived };
